import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Textarea } from '@/shared/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { toast } from 'sonner';
import { Globe, ArrowLeft, Save, Languages, Check, AlertCircle } from 'lucide-react';
import useLandingConfig from '../hooks/useLandingConfig';

const LANGUAGES = [
{ code: 'en', name: 'English', flag: '🇺🇸' },
{ code: 'pt-br', name: 'Portuguese (Brazil)', flag: '🇧🇷' },
{ code: 'es', name: 'Spanish', flag: '🇪🇸' }];


export const TranslationsManager = () => {const { t } = useTranslation("landing");
  const { config, sections, loading, updateTranslation, fetchConfig } = useLandingConfig();
  const [selectedLanguage, setSelectedLanguage] = useState('pt-br');
  const [translations, setTranslations] = useState({});
  const [saving, setSaving] = useState({});
  const [allBlocks, setAllBlocks] = useState([]);

  useEffect(() => {
    if (sections) {
      // Flatten all content blocks from all sections
      const blocks = sections.flatMap((section) =>
      (section.content_blocks || []).map((block) => ({
        ...block,
        sectionKey: section.section_key,
        sectionType: section.section_type
      }))
      );
      setAllBlocks(blocks);

      // Initialize translations state
      const initialTranslations = {};
      blocks.forEach((block) => {
        const existingTrans = block.translations?.[selectedLanguage] || {};
        initialTranslations[block.id] = {
          title: existingTrans.title || '',
          subtitle: existingTrans.subtitle || '',
          description: existingTrans.description || '',
          cta_text: existingTrans.cta_text || ''
        };
      });
      setTranslations(initialTranslations);
    }
  }, [sections, selectedLanguage]);

  const handleTranslationChange = (blockId, field, value) => {
    setTranslations((prev) => ({
      ...prev,
      [blockId]: {
        ...prev[blockId],
        [field]: value
      }
    }));
  };

  const handleSaveTranslation = async (blockId) => {
    setSaving((prev) => ({ ...prev, [blockId]: true }));

    const result = await updateTranslation(blockId, {
      language: selectedLanguage,
      ...translations[blockId]
    });

    setSaving((prev) => ({ ...prev, [blockId]: false }));

    if (result.success) {
      toast.success('Translation saved!');
    } else {
      toast.error(result.error || 'Failed to save translation');
    }
  };

  const handleSaveAllTranslations = async () => {
    const blocksToSave = allBlocks.filter((block) => {
      const trans = translations[block.id];
      return trans && (trans.title || trans.subtitle || trans.description || trans.cta_text);
    });

    let successCount = 0;
    for (const block of blocksToSave) {
      const result = await updateTranslation(block.id, {
        language: selectedLanguage,
        ...translations[block.id]
      });
      if (result.success) successCount++;
    }

    if (successCount === blocksToSave.length) {
      toast.success(`All ${successCount} translations saved!`);
    } else {
      toast.warning(`Saved ${successCount}/${blocksToSave.length} translations`);
    }
  };

  const getTranslationProgress = (blockId) => {
    const trans = translations[blockId] || {};
    const block = allBlocks.find((b) => b.id === blockId);
    if (!block) return 0;

    const fields = ['title', 'subtitle', 'description', 'cta_text'];
    const filledFields = fields.filter((f) => trans[f]?.trim());
    const sourceFields = fields.filter((f) => block[f]?.trim());

    if (sourceFields.length === 0) return 100;
    return Math.round(filledFields.length / sourceFields.length * 100);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>);

  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" asChild>
            <a href="/admin/landing">
              <ArrowLeft className="w-5 h-5" />
            </a>
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Globe className="w-6 h-6 text-green-500" />{t("translations", "Translations")}

            </h1>
            <p className="text-slate-400">{t("manageMultilanguageContent", "Manage multi-language content")}</p>
          </div>
        </div>
        <Button onClick={handleSaveAllTranslations} className="gap-2" data-testid="save-all-translations-btn">
          <Save className="w-4 h-4" />{t("saveAllTranslations", "Save All Translations")}

        </Button>
      </div>

      {/* Language Selector */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <Label className="text-white">{t("targetLanguage", "Target Language:")}</Label>
            <div className="flex gap-2">
              {LANGUAGES.filter((lang) => lang.code !== 'en' && config?.enabled_languages?.includes(lang.code)).map((lang) =>
              <Button
                key={lang.code}
                variant={selectedLanguage === lang.code ? 'default' : 'outline'}
                onClick={() => setSelectedLanguage(lang.code)}
                className="gap-2"
                data-testid={`lang-btn-${lang.code}`}>
                
                  <span>{lang.flag}</span>
                  {lang.name}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Translation Progress Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {allBlocks.length > 0 &&
        <>
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-400">{t("totalBlocks", "Total Blocks")}</p>
                    <p className="text-2xl font-bold text-white">{allBlocks.length}</p>
                  </div>
                  <Languages className="w-8 h-8 text-slate-500" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-400">{t("fullyTranslated", "Fully Translated")}</p>
                    <p className="text-2xl font-bold text-green-500">
                      {allBlocks.filter((b) => getTranslationProgress(b.id) === 100).length}
                    </p>
                  </div>
                  <Check className="w-8 h-8 text-green-500" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-400">{t("needsTranslation", "Needs Translation")}</p>
                    <p className="text-2xl font-bold text-yellow-500">
                      {allBlocks.filter((b) => getTranslationProgress(b.id) < 100).length}
                    </p>
                  </div>
                  <AlertCircle className="w-8 h-8 text-yellow-500" />
                </div>
              </CardContent>
            </Card>
          </>
        }
      </div>

      {/* Content Blocks Translations */}
      {sections.map((section) => {
        const sectionBlocks = allBlocks.filter((b) => b.sectionKey === section.section_key);
        if (sectionBlocks.length === 0) return null;

        return (
          <Card key={section.id} className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white capitalize flex items-center gap-2">
                {section.section_key.replace(/_/g, ' ')}
                <Badge variant="outline">{sectionBlocks.length}{t("blocks", "blocks")}</Badge>
              </CardTitle>
              <CardDescription className="text-slate-400">
                {section.section_type}{t("section", "section")}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {sectionBlocks.map((block) =>
              <div key={block.id} className="p-4 bg-slate-700/30 rounded-lg border border-slate-600">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <h4 className="font-medium text-white">{block.block_key}</h4>
                      <Badge
                      variant={getTranslationProgress(block.id) === 100 ? 'default' : 'secondary'}
                      className={getTranslationProgress(block.id) === 100 ? 'bg-green-500/20 text-green-500' : ''}>
                      
                        {getTranslationProgress(block.id)}{t("translated", "% translated")}
                    </Badge>
                    </div>
                    <Button
                    size="sm"
                    onClick={() => handleSaveTranslation(block.id)}
                    disabled={saving[block.id]}
                    className="gap-2">
                    
                      {saving[block.id] ? 'Saving...' : <><Save className="w-3 h-3" />{t("save", "Save")}</>}
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Source (English) */}
                    <div className="space-y-3">
                      <Label className="text-slate-400 text-xs flex items-center gap-1">{t("englishSource", "\uD83C\uDDFA\uD83C\uDDF8 English (Source)")}

                    </Label>
                      {block.title &&
                    <div className="p-2 bg-slate-600/30 rounded text-sm text-slate-300">
                          <span className="text-xs text-slate-500 block mb-1">{t("title", "Title:")}</span>
                          {block.title}
                        </div>
                    }
                      {block.subtitle &&
                    <div className="p-2 bg-slate-600/30 rounded text-sm text-slate-300">
                          <span className="text-xs text-slate-500 block mb-1">{t("subtitle", "Subtitle:")}</span>
                          {block.subtitle}
                        </div>
                    }
                      {block.description &&
                    <div className="p-2 bg-slate-600/30 rounded text-sm text-slate-300">
                          <span className="text-xs text-slate-500 block mb-1">{t("description", "Description:")}</span>
                          {block.description}
                        </div>
                    }
                      {block.cta_text &&
                    <div className="p-2 bg-slate-600/30 rounded text-sm text-slate-300">
                          <span className="text-xs text-slate-500 block mb-1">{t("ctaText", "CTA Text:")}</span>
                          {block.cta_text}
                        </div>
                    }
                    </div>

                    {/* Target Language */}
                    <div className="space-y-3">
                      <Label className="text-slate-400 text-xs flex items-center gap-1">
                        {LANGUAGES.find((l) => l.code === selectedLanguage)?.flag} {LANGUAGES.find((l) => l.code === selectedLanguage)?.name}{t("translation", "(Translation)")}
                    </Label>
                      {block.title &&
                    <div>
                          <span className="text-xs text-slate-500 block mb-1">{t("title", "Title:")}</span>
                          <Input
                        value={translations[block.id]?.title || ''}
                        onChange={(e) => handleTranslationChange(block.id, 'title', e.target.value)}
                        className="bg-slate-700 border-slate-600 text-white"
                        placeholder="Enter translation..." />
                      
                        </div>
                    }
                      {block.subtitle &&
                    <div>
                          <span className="text-xs text-slate-500 block mb-1">{t("subtitle", "Subtitle:")}</span>
                          <Input
                        value={translations[block.id]?.subtitle || ''}
                        onChange={(e) => handleTranslationChange(block.id, 'subtitle', e.target.value)}
                        className="bg-slate-700 border-slate-600 text-white"
                        placeholder="Enter translation..." />
                      
                        </div>
                    }
                      {block.description &&
                    <div>
                          <span className="text-xs text-slate-500 block mb-1">{t("description", "Description:")}</span>
                          <Textarea
                        value={translations[block.id]?.description || ''}
                        onChange={(e) => handleTranslationChange(block.id, 'description', e.target.value)}
                        className="bg-slate-700 border-slate-600 text-white"
                        placeholder="Enter translation..."
                        rows={3} />
                      
                        </div>
                    }
                      {block.cta_text &&
                    <div>
                          <span className="text-xs text-slate-500 block mb-1">{t("ctaText", "CTA Text:")}</span>
                          <Input
                        value={translations[block.id]?.cta_text || ''}
                        onChange={(e) => handleTranslationChange(block.id, 'cta_text', e.target.value)}
                        className="bg-slate-700 border-slate-600 text-white"
                        placeholder="Enter translation..." />
                      
                        </div>
                    }
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>);

      })}

      {allBlocks.length === 0 &&
      <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="py-12 text-center">
            <Languages className="w-12 h-12 text-slate-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">{t("noContentTo", "No content to translate")}</h3>
            <p className="text-slate-400">{t("addContentBlocks", "Add content blocks to your sections first")}</p>
          </CardContent>
        </Card>
      }
    </div>);

};

export default TranslationsManager;